<!--
    Mobile install gate — full-screen overlay shown when the user
    is on a mobile browser (non-standalone). Sits above everything
    in the app so it gates the sign-in screen too, per the
    "must happen before login" requirement.

    Layout: centered card with logo, headline, body copy, install
    affordance (button or instructions), and a small "Continue in
    browser anyway" escape link.
-->
<div *ngIf="visible" class="mig" role="dialog" aria-modal="true" aria-labelledby="mig-title">
    <div class="mig__backdrop" aria-hidden="true"></div>

    <div class="mig__card" [class.mig__card--open-mode]="showOpenFromHomeScreen">
        <!-- Brand row -->
        <div class="mig__brand">
            <img src="images/logo/dp_logo.png" alt="District Printing" class="mig__logo" />
            <span class="mig__brand-name">District Printing</span>
        </div>

        <!--
            IN-APP BROWSER WARNING — user came to us from inside an
            in-app webview (Instagram / Facebook / TikTok / LinkedIn
            / Messenger / etc). PWA install is IMPOSSIBLE in those
            webviews — no `beforeinstallprompt`, no manifest install
            menu, nothing the app exposes can fix it. The only path
            forward is "open this URL in Chrome / Safari first", so
            we say that loudly with a copy-URL helper instead of
            showing the install flow that can't possibly work.

            This is the single largest cause of "I see the install
            page but there's no button" reports — most users tap a
            shared link from Instagram DMs or a Facebook post and
            never escape that app's webview.
        -->
        <ng-container *ngIf="isInAppBrowser && !showOpenFromHomeScreen">
            <h1 id="mig-title" class="mig__title">Open this in your browser</h1>
            <p class="mig__subtitle">
                You're inside the <strong>{{ inAppBrowserLabel }}</strong> app's
                browser. Installing isn't available here — please open this
                page in <strong>{{ isIos ? 'Safari' : 'Chrome' }}</strong>
                to continue.
            </p>
            <div class="mig__open-card">
                <mat-icon class="mig__open-card-icon">open_in_browser</mat-icon>
                <ol class="mig__open-card-steps">
                    <li>
                        Tap the
                        <mat-icon class="mig__inline-icon">more_vert</mat-icon>
                        menu (three dots) at the top of this screen
                    </li>
                    <li>
                        Choose <strong>{{ isIos ? 'Open in Safari' : 'Open in Chrome' }}</strong>
                        (or "Open in browser")
                    </li>
                    <li>Come back to this page in your real browser</li>
                </ol>
            </div>
            <button
                type="button"
                mat-flat-button
                class="mig__install-btn"
                (click)="copyLink()"
            >
                <mat-icon class="mig__install-icon">content_copy</mat-icon>
                <span>Copy link to paste</span>
            </button>
            <button type="button" class="mig__claim" (click)="iHaveItInstalled()">
                I already have the app installed
            </button>
            <button type="button" class="mig__skip" (click)="continueInBrowser()">
                Continue here anyway
            </button>
        </ng-container>

        <!--
            STRONG STATE — user has the app installed but is in the
            browser tab. Browsers don't expose a "launch my PWA"
            API, so we can't auto-redirect; the best we can do is
            tell them clearly to leave the browser and open the
            home-screen icon instead.

            Reached via:
              * `detectedInstalled` — Chromium / Android with the
                manifest `related_applications` self-reference;
                navigator.getInstalledRelatedApps() came back
                non-empty.
              * `userClaimsInstalled` — user tapped the "I already
                have it installed" button below. Honor self-report
                because browser detection misses every iOS user and
                most cross-browser installs.
        -->
        <ng-container *ngIf="showOpenFromHomeScreen">
            <h1 id="mig-title" class="mig__title">Open the app from your home screen</h1>
            <p class="mig__subtitle">
                <ng-container *ngIf="detectedInstalled">
                    We detected the app is installed on this device, but you're
                    in a browser tab.
                </ng-container>
                <ng-container *ngIf="!detectedInstalled">
                    You already have the app installed.
                </ng-container>
                Close this tab and tap the
                <strong>District Printing</strong> icon on your home screen for
                the full experience.
            </p>
            <div class="mig__open-card">
                <mat-icon class="mig__open-card-icon">smartphone</mat-icon>
                <ol class="mig__open-card-steps">
                    <li>Close this browser tab</li>
                    <li>Go to your home screen</li>
                    <li>
                        Tap the <strong>District Printing</strong> icon (it
                        opens full-screen, no browser bar)
                    </li>
                </ol>
            </div>
            <button
                type="button"
                class="mig__skip"
                (click)="cancelClaimInstalled()"
            >
                Actually, I haven't installed it yet
            </button>
        </ng-container>

        <!--
            DEFAULT STATE — user is on mobile web and hasn't told us
            they're installed (and we couldn't auto-detect), AND we
            haven't trapped them in an in-app browser. Show the
            normal install affordances + a self-report button.
        -->
        <ng-container *ngIf="!showOpenFromHomeScreen && !isInAppBrowser">
        <!-- Headline -->
        <h1 id="mig-title" class="mig__title">Install the app for the best experience</h1>
        <p class="mig__subtitle">
            The mobile web view is limited. Install the app to your home
            screen for a faster, full-screen experience without browser
            chrome.
        </p>

        <!--
            iOS: no `beforeinstallprompt` support — we surface the
            Share → Add to Home Screen steps with an expand toggle.
        -->
        <div *ngIf="isIos" class="mig__ios">
            <button
                type="button"
                class="mig__ios-toggle"
                (click)="toggleIosHint()"
                [attr.aria-expanded]="iosHintExpanded"
            >
                <span class="mig__ios-toggle-left">
                    <mat-icon class="mig__ios-icon">install_mobile</mat-icon>
                    <span>How to install on iPhone / iPad</span>
                </span>
                <mat-icon class="mig__ios-chev">
                    {{ iosHintExpanded ? 'expand_less' : 'expand_more' }}
                </mat-icon>
            </button>
            <ol *ngIf="iosHintExpanded" class="mig__ios-steps">
                <li>
                    Tap the
                    <mat-icon class="mig__inline-icon">ios_share</mat-icon>
                    Share button in Safari
                </li>
                <li>Scroll down and tap <strong>Add to Home Screen</strong></li>
                <li>Tap <strong>Add</strong> in the top right corner</li>
                <li>Open the new app icon from your home screen to start</li>
            </ol>
        </div>

        <!--
            Android / Chromium mobile: install button if we have a
            captured prompt, otherwise a fallback hint pointing at
            the browser's three-dot menu.
        -->
        <div *ngIf="!isIos" class="mig__android">
            <button
                *ngIf="canPromptInstall"
                type="button"
                mat-flat-button
                class="mig__install-btn"
                (click)="install()"
            >
                <mat-icon class="mig__install-icon">install_mobile</mat-icon>
                <span>Install App</span>
            </button>

            <div *ngIf="!canPromptInstall" class="mig__android-fallback">
                <p class="mig__android-fallback-title">
                    <mat-icon class="mig__android-fallback-icon">menu</mat-icon>
                    To install manually:
                </p>
                <!--
                    Browser-specific steps. Each Android browser puts
                    the install option in a different menu (Samsung
                    Internet's is at the bottom, Edge calls it "Add
                    to phone", Firefox sometimes doesn't have one at
                    all), so we render the steps that match the
                    actual browser we detected instead of the
                    "three dots, top right" lie that fits Chrome
                    only.
                -->
                <ol class="mig__android-fallback-steps" [ngSwitch]="browser">
                    <ng-container *ngSwitchCase="'chrome'">
                        <li>
                            Tap the
                            <mat-icon class="mig__inline-icon">more_vert</mat-icon>
                            menu (three dots, top right)
                        </li>
                        <li>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong></li>
                        <li>Confirm <strong>Install</strong></li>
                    </ng-container>
                    <ng-container *ngSwitchCase="'samsung'">
                        <li>
                            Tap the
                            <mat-icon class="mig__inline-icon">menu</mat-icon>
                            menu (three lines, bottom right)
                        </li>
                        <li>Tap <strong>Add page to</strong> → <strong>Home screen</strong></li>
                        <li>Confirm <strong>Add</strong></li>
                    </ng-container>
                    <ng-container *ngSwitchCase="'edge'">
                        <li>
                            Tap the
                            <mat-icon class="mig__inline-icon">more_horiz</mat-icon>
                            menu (three dots, bottom)
                        </li>
                        <li>Tap <strong>Add to phone</strong></li>
                        <li>Confirm <strong>Add</strong></li>
                    </ng-container>
                    <ng-container *ngSwitchCase="'firefox'">
                        <li>
                            Tap the
                            <mat-icon class="mig__inline-icon">more_vert</mat-icon>
                            menu (three dots, bottom right)
                        </li>
                        <li>Tap <strong>Install</strong> or <strong>Add to Home screen</strong></li>
                        <li>
                            If neither option is there, Firefox may
                            not support this — try
                            <strong>Chrome</strong> instead.
                        </li>
                    </ng-container>
                    <ng-container *ngSwitchCase="'brave'">
                        <li>
                            Tap the
                            <mat-icon class="mig__inline-icon">more_vert</mat-icon>
                            menu (three dots, top right)
                        </li>
                        <li>Tap <strong>Add to Home screen</strong></li>
                    </ng-container>
                    <ng-container *ngSwitchDefault>
                        <li>Tap your browser's menu (three dots or three lines)</li>
                        <li>Look for <strong>Install app</strong> or <strong>Add to Home screen</strong></li>
                        <li>
                            If neither appears, open this page in
                            <strong>Chrome</strong> for the best result.
                        </li>
                    </ng-container>
                </ol>
            </div>
        </div>

        <!--
            "Already installed" self-report. Browsers can't reliably
            detect every install (Safari has zero APIs, Firefox
            none, only Chromium/Android with our manifest's
            related_applications can). When detection misses, the
            user can tap this button to flip the gate into the
            strong "open from your home screen" message.

            Sits above the skip link so it reads as a primary
            answer to "what if I already installed?" — the skip
            link below is purely the emergency exit.
        -->
        <button
            type="button"
            class="mig__claim"
            (click)="iHaveItInstalled()"
        >
            I already have the app installed
        </button>

        <!--
            Soft-gate escape hatch. Session-scoped so the gate
            quietly returns on the user's next visit — gentle
            pressure to install without nagging within a session.
        -->
        <button
            type="button"
            class="mig__skip"
            (click)="continueInBrowser()"
        >
            Continue in browser anyway
        </button>
        </ng-container>
    </div>
</div>
